import type { NotificationType } from '@/api/shell';
import i18n from '@/i18n';

/**
 * EAA-1813: presentation helpers shared by the two shell-chrome popovers
 * (`CommandPalette`, `NotificationPopover`). They live in a plain `.ts` sibling
 * rather than inside either `.tsx` so `react-refresh/only-export-components`
 * stays satisfied — those files export components only.
 *
 * EAA-1845: no component/hook here either, so translated strings go through
 * the shared `i18n` instance directly (`i18n.t`), not `useTranslation()`.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * EAA-1813: frame 14's timestamps are minute-granular ("12 phút trước") and
 * carry a "Hôm qua" step. `formatCatalogAge` (pages/console/apps/catalogAge.ts)
 * is day-granular and floors everything under 24h to "Hôm nay", so it cannot
 * render this frame — hence a second formatter rather than a reuse.
 */
export function formatNotificationAge(iso: string): string {
    const at = new Date(iso).getTime();
    if (Number.isNaN(at)) return '';
    const diff = Date.now() - at;
    if (diff < MINUTE_MS) return i18n.t('shell.notifications.ageJustNow');
    if (diff < HOUR_MS) {
        return i18n.t('shell.notifications.ageMinutesAgo', {
            count: Math.floor(diff / MINUTE_MS),
        });
    }
    if (diff < DAY_MS) {
        return i18n.t('shell.notifications.ageHoursAgo', {
            count: Math.floor(diff / HOUR_MS),
        });
    }
    const days = Math.floor(diff / DAY_MS);
    if (days === 1) return i18n.t('shell.notifications.ageYesterday');
    return i18n.t('shell.notifications.ageDaysAgo', { count: days });
}

export type NotificationTone = 'error' | 'warning';

/**
 * EAA-1813: frame 14 draws a coloured dot on every notification row, which
 * reads as a severity ramp (red / amber / green). `PortalNotification` has NO
 * severity, level, priority or status field — only `is_read`/`read_at` (see
 * api/shell.ts, mirroring `NotificationSerializer`). So the dot is keyed on the
 * one real classifier the API does send, `notification_type`, rather than on an
 * invented severity: the two failure kinds take the danger tone, the two
 * "needs attention" kinds take the warning tone.
 *
 * The frame's green row ("Đồng bộ catalog hoàn tất") has no counterpart in the
 * `NotificationType` union at all, so no success tone is mapped — the backend
 * never emits an informational notification today.
 */
const NOTIFICATION_TONES: Record<NotificationType, NotificationTone> = {
    role_map_paused: 'error',
    assignment_sync_error: 'error',
    app_pending_approval: 'warning',
    orphan_level: 'warning',
};

export function notificationTone(type: NotificationType): NotificationTone {
    return NOTIFICATION_TONES[type] ?? 'warning';
}

/**
 * EAA-1813: frame 13 gives every palette row a two-letter initials tile. Splits
 * on separators as well as whitespace so an email/username ("gv.tan.an") yields
 * a tile too, not just a multi-word org name ("Trường Tiểu học Tân An" → TA).
 */
export function initialsOf(label: string): string {
    const words = label.split(/[\s._@-]+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
